<template>
  <AntSelect
    class="customized-version-select"
    :style="{
      width: '100%',
      color: selectedDocId && selectedDocId !== 'general' ? '#1890ff' : 'rgba(0, 0, 0, 0.5)',
      // '--selector-border-color': options.length > 1 ? '#40a9ff' : '#d9d9d9',
      '--selector-background-color': options.length > 1 ? '#e6f7ff' : '#f1f1f1',
    }"
    v-bind="$attrs"
    :value="selectedDocId || 'general'"
    :options="options"
    showSearch
    @change="val => $emit('update:selectedDocId', val)"
    :open="customizedVersionState.selectedDocIdDropdownOpen"
    @focus="customizedVersionState.selectedDocIdDropdownOpen = true"
    @dropdownVisibleChange="handleSelectedDocIdDropdownVisibleChange"
  >
    <template #option="{ label, value }">
      <div style="display: flex; align-items: center; justify-content: space-between;">
        <div style="overflow: hidden;">
          {{ label }}
        </div>

        <AntPopconfirm
          v-if="value !== 'general'"
          title="Sure to delete?"
          @confirm="e => {
            onClickRemoveCustomizedVersion(e, value)
            if (selectedDocId === value) $emit('update:selectedDocId', 'general')
          }"
        >
          <DeleteIcon
            class="delete-customized-version-icon"
            title="Delete"
            @click.stop
          />
        </AntPopconfirm>
      </div>
    </template>

    <template #dropdownRender="{ menuNode: menu }">
      <VNodes :vnodes="menu" />
      <template v-if="toAddDocIdOptions.length">
        <AntDivider style="margin: 4px 0" />
        <div style="display: flex; align-items: center; gap: 8px; padding: 10px;">
          <AntSelect
            style="width: 100%"
            :value="customizedVersionState.toAddDocId"
            :options="toAddDocIdOptions"
            placeholder="Select an API doc to add customized version"
            showSearch
            @focus="customizedVersionState.toAddDocIdDropdownOpen = true"
            @change="val => {
              customizedVersionState.toAddDocId = val
              customizedVersionState.toAddDocIdDropdownOpen = false
            }"
            @dropdownVisibleChange="val => { customizedVersionState.toAddDocIdDropdownOpen = val }"
            @mousedown.stop
            @click.stop
          />

          <AntButton
            type="primary"
            shape="circle"
            size="small"
            title="Add Customized Version"
            :disabled="!customizedVersionState.toAddDocId"
            @click.stop="() => {
              const docId = customizedVersionState.toAddDocId
              if (!docId) return
              onClickAddCustomizedVersion(docId)
              customizedVersionState.toAddDocId = undefined
              customizedVersionState.selectedDocIdDropdownOpen = false
              $emit('update:selectedDocId', docId)
            }"
          >
            <template #icon>
              <AddIcon />
            </template>
          </AntButton>
        </div>
      </template>
    </template>
  </AntSelect>
</template>

<script>
import { reactive, computed, watch } from 'vue'
import { cloneDeep } from 'lodash'

import {
  Select as AntSelect,
  Divider as AntDivider,
  Button as AntButton,
  Popconfirm as AntPopconfirm,
} from 'ant-design-vue'

import AddIcon from '@ant-design/icons-vue/lib/icons/PlusOutlined'
import DeleteIcon from '@ant-design/icons-vue/lib/icons/DeleteOutlined'

import { shouldDisplay } from '../lib/libsV3'

export default {
  name: 'CustomizedVersionSelect',
  components: {
    AntSelect,
    AntDivider,
    AntButton,
    AntPopconfirm,
    AddIcon,
    DeleteIcon,
    VNodes: (_, { attrs }) => attrs.vnodes,
  },
  props: {
    selectedDocId: String,
    docsDataByDocId: {
      type: Object,
      default: () => ({}),
    },
    selectedSectionState: Object,
    selectedSectionOrFieldState: Object,
  },
  emits: ['update:selectedDocId'],
  setup(props) {
    const selectedSectionOrFieldState = computed(() => props.selectedSectionOrFieldState || {})

    const customizedVersionState = reactive({
      toAddDocId: undefined,
      selectedDocIdDropdownOpen: false,
      toAddDocIdDropdownOpen: false,
    })

    /** 僅 UI：使用者在此編輯目標上按 Add 的 docId（不寫入 section／field 資料） */
    const userListedCustomizedDocIds = reactive(new Set())

    watch(
      () => props.selectedSectionOrFieldState,
      () => userListedCustomizedDocIds.clear(),
    )

    /** 母本拖曳同步的子本 sortOrder 殘留：僅 { docId, sortOrder }，不列入可切換版本 */
    const isSortOrderOnlyCustomizedEntry = (cv = {}) => {
      const keys = Object.keys(cv).filter(k => k !== 'docId')
      return keys.length === 1 && keys[0] === 'sortOrder' && cv.sortOrder !== undefined
    }

    const customizedVersionDocIds = computed(() => {
      const result = []
      Object.entries(selectedSectionOrFieldState.value?.customizedVersion || {}).forEach(([docId, cv]) => {
        if (isSortOrderOnlyCustomizedEntry(cv) && !userListedCustomizedDocIds.has(docId)) return
        result.push(docId)
      })
      return result
    })

    const toAddDocIdOptions = computed(() => {
      const docs = props.selectedSectionState?.docs || []
      return docs
        // shouldDisplay 過濾子本 Platforms
        .filter(docId => !customizedVersionDocIds.value.includes(docId) && shouldDisplay(props.selectedSectionState, props.docsDataByDocId[docId]))
        .map(docId => ({ value: docId, label: props.docsDataByDocId[docId]?.docShortTitle }))
    })

    const options = computed(() => [
      { value: 'general', label: 'General' },
      ...customizedVersionDocIds.value.map(docId => ({
        value: docId,
        label: props.docsDataByDocId[docId]?.docShortTitle,
      })),
    ])

    const handleSelectedDocIdDropdownVisibleChange = (val) => {
      // 當內層 toAddDocId dropdown 還開著時，避免外層關閉導致內層選擇中斷
      if (
        !val
        && (customizedVersionState.toAddDocId || customizedVersionState.toAddDocIdDropdownOpen)
      ) {
        customizedVersionState.selectedDocIdDropdownOpen = true
      } else {
        customizedVersionState.selectedDocIdDropdownOpen = val
      }
    }

    const onClickAddCustomizedVersion = (docId) => {
      const target = selectedSectionOrFieldState.value
      if (!target || !docId) return
      if (!target.customizedVersion) target.customizedVersion = {}

      const prev = target.customizedVersion[docId]
      let entry = { ...(prev || {}), docId }

      // Section／Appendix：子本 content 預設為 General 的 content
      if (target.sectionId != null) {
        if (!Object.prototype.hasOwnProperty.call(entry, 'content')
          && Object.prototype.hasOwnProperty.call(target, 'content')) {
          entry.content = target.content
        }
      } else if (target.fieldId != null && (!prev || isSortOrderOnlyCustomizedEntry(prev))) {
        // Field：預設複製 General 欄位（不含 customizedVersion）
        const generalField = cloneDeep(target)
        delete generalField.customizedVersion
        entry = { ...generalField, ...entry, docId }
      } else if (!prev) {
        entry = { docId }
      }

      target.customizedVersion[docId] = entry

      if (prev && isSortOrderOnlyCustomizedEntry(prev) && !Object.prototype.hasOwnProperty.call(entry, 'content')) {
        userListedCustomizedDocIds.add(docId)
      }
    }

    const onClickRemoveCustomizedVersion = (e, docId) => {
      if (!selectedSectionOrFieldState.value || !selectedSectionOrFieldState.value.customizedVersion) return
      e.stopPropagation()
      userListedCustomizedDocIds.delete(docId)
      if (selectedSectionOrFieldState.value.customizedVersion[docId].sortOrder !== undefined) {
        selectedSectionOrFieldState.value.customizedVersion[docId] = { docId, sortOrder: selectedSectionOrFieldState.value.customizedVersion[docId].sortOrder }
      } else {
        delete selectedSectionOrFieldState.value.customizedVersion[docId]
      }
    }

    return {
      customizedVersionState,
      toAddDocIdOptions,
      options,
      handleSelectedDocIdDropdownVisibleChange,
      onClickAddCustomizedVersion,
      onClickRemoveCustomizedVersion,
    }
  },
}
</script>

<style scoped>
.delete-customized-version-icon:hover {
  color: #1890ff;
}

.customized-version-select :deep(.ant-select-selector) {
  background-color: var(--selector-background-color)!important;
  border-color: var(--selector-background-color)!important;
}
</style>
